import { observer } from 'mobx-react-lite'
import { useState, useMemo } from 'react'
import { ModalWindow } from 'src/component/common/analog/template/ModalWindow'
import { AppBox } from 'src/component/common/analog/molecule/AppBox'
import { Typography, Fade, useTheme } from '@mui/material'
import { SkillCategory, SkillSet } from '../../../skill-assessment/model/skill-assessment-model'
import { SkillRatingGrid } from '../organism/SkillRatingGrid'
import { AppButton } from 'src/component/common/analog/molecule/button/AppButton'
import { SkillAssessmentLevels } from 'src/feature/skill-assessment/component/SkillAssessmentLevels'
import { SkillRatingModalContext } from '../../hooks/useSkillRatingModal'
import { useLocalizationStore } from 'src/feature/localization/store/localization.store'

/**
 * Props for CategoryRatingModal component
 */
interface CategoryRatingModalProps {
  category: SkillCategory
  isOpen: boolean
  onClose: () => void
  errorMessage: string
}

/**
 * Modal that displays the full rating interface for a single category
 * Contains skill grid and rating functionality
 */
export const CategoryRatingModal = observer(({
  category,
  isOpen,
  onClose,
  errorMessage,
}: CategoryRatingModalProps) => {
  const theme = useTheme()
  const { translation } = useLocalizationStore()
  const translations = translation.pages.myTeam.rolesButton
  const [isAssessmentModalOpen, setIsAssessmentModalOpen] = useState<boolean>(false)
  const [skillAssessmentSetIndex, setSkillAssessmentSetIndex] = useState<number>(0)

  const checkIfAllSkillsAreRated = (): boolean => category?.skills?.every((skill: SkillSet) => skill.selectedSetLevel !== null) ?? false

  const handleRateClick = () => {
    setSkillAssessmentSetIndex(0)
    setIsAssessmentModalOpen(true)
  }

  const handleAssessmentModalClose = () => {
    setIsAssessmentModalOpen(false)
  }

  const modalContext = useMemo(() => ({
    handleRateSkillClick: (selectedSkillSetIndex: number) => {
      setSkillAssessmentSetIndex(selectedSkillSetIndex)
      setIsAssessmentModalOpen(true)
    },
  }), [])

  return (
    <SkillRatingModalContext.Provider value={modalContext}>
      <ModalWindow
        modalProps={{
          open: isOpen,
          onClose,
          PaperProps: {
            sx: {
              maxWidth: { xs: '90%', sm: '600px', md: '800px', lg: '1040px' },
              borderRadius: '10px',
            },
          },
        }}
        titleProps={{
          textValue: category.category_name,
        }}
        subtitleProps={{
          textValue: category.category_description,
          sx: { color: 'text.secondary' },
        }}
      >
        <AppBox sx={styles.container}>
          <Fade in={!checkIfAllSkillsAreRated()} timeout={700} mountOnEnter unmountOnExit>
            <Typography sx={styles.errorMessage}>
              {errorMessage}
            </Typography>
          </Fade>
          <SkillRatingGrid
            skills={category.skills}
            isAllSkillsRated={checkIfAllSkillsAreRated()}
            defaultColor={theme.palette.background.paper}
          />
          <AppButton
            fontSize={12}
            variant='contained'
            sx={{ alignSelf: 'center', width: '10em' }}
            onClick={handleRateClick}
          >
            {checkIfAllSkillsAreRated() ? translations.updateSkill() : translations.rateSkill()}
          </AppButton>
        </AppBox>
      </ModalWindow>
      {isAssessmentModalOpen && (
        <SkillAssessmentLevels
          skillAssessmentSetIndex={skillAssessmentSetIndex}
          isModalOpen={isAssessmentModalOpen}
          isModalClose={handleAssessmentModalClose}
          categoryId={category.id}
        />
      )}
    </SkillRatingModalContext.Provider>
  )
})

const styles = {
  container: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    justifyContent: 'center',
    gap: 20,
  },
  errorMessage: {
    fontSize: 16,
    color: 'error.main',
  },
}
